import React, { useState } from 'react';
import * as Styles from '../style';
import { Icon } from 'semantic-ui-react';
import { H4, H6, P } from 'components/reusable-css';
import { useTranslation } from 'react-i18next';
import Input from 'components/input';
import CustomButton from 'components/common/custom-button';
import { useMutation, useQuery } from 'react-query';
import { ADD_OBJECTS, DELETE_ALL_OBJECT } from '../queries';
import { mutationProps } from 'utils/custom-hook';
import { Form, Formik } from 'formik';
import { addObjectInitial, addObjectValidationSchema } from '../utils';
import Loading from 'components/common/loading';

const CreateObjectPanel = ({ label, handleCloseModal, item, mainRefetch = () => {} }) => {
  const { t } = useTranslation();
  const originalArray = item?.list?.firewall_object_records || [];
  const [ipNumberArr, setIpNumberArr] = useState(originalArray);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const { mutate: vsObjectAction, isLoading: vsObjectActionLoading } = useMutation(DELETE_ALL_OBJECT);
  const [isFocusField, setIsFocusField] = useState(undefined);
  const {
    data: ObjectsArr,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery(`/vs/firewall/object/${item?.id}`, {
    enabled: !!item?.id,
    onSuccess: (res) => {
      setIpNumberArr(res?.firewall_object_records);
    },
  });
  const handleClick = (values, resetForm) => {
    setIpNumberArr((_) => [..._, { protocol: '', value: values.ipnumber }]);
    setIsFocusField('firewall_object_ipnumber_port');
    resetForm();
    setIsFocusField(undefined);
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();

    // get Y position relative to screen
    const mouseY = e.clientY;

    // find the hovered element's middle point
    const targetRect = e.currentTarget.getBoundingClientRect();
    const targetMiddleY = targetRect.top + targetRect.height / 2;

    // only swap when crossing over the middle (up or down)
    if (index !== draggedIndex) {
      if ((draggedIndex < index && mouseY > targetMiddleY) || (draggedIndex > index && mouseY < targetMiddleY)) {
        const updated = [...ipNumberArr];
        const [draggedItem] = updated.splice(draggedIndex, 1);
        updated.splice(index, 0, draggedItem);

        // reassign order numbers
        const reOrdered = updated.map((value) => ({
          ...value,
        }));
        setDraggedIndex(index);
        setIpNumberArr(reOrdered);
      }
    }
  };

  const handleDelete = (index) => {
    const newCards = ipNumberArr.map((card, i) => {
      if (i === index) {
        return {
          ...card,
          isDeleted: 1, // mark as deleted
        };
      }
      return card;
    });

    setIpNumberArr(newCards);
  };

  const handleCommit = async () => {
    await vsObjectAction(
      {
        id: item?.id,
        action: 'clear',
      },
      mutationProps(
        false,
        {
          onSuccess: async () => {
            try {
              const promises = [];
              const filterDeletedArray = ipNumberArr?.filter((data) => data.isDeleted != 1);
              for (const key in filterDeletedArray) {
                const object = filterDeletedArray[key];
                const promise = await ADD_OBJECTS(object, item?.id);
                promises.push(promise);
              }
              promises.length > 0 && (await Promise.all(promises));
              await vsObjectAction(
                {
                  id: item?.id,
                  action: 'commit',
                },
                mutationProps(
                  false,
                  {
                    onSuccess: () => {
                      refetch();
                      mainRefetch();
                    },
                  },
                  t,
                ),
              );
            } catch (error) {
              console.log(error);
            }
          },
        },
        t,
      ),
    );
  };

  if (isLoading || !item.id) {
    return <Loading gap={50} />;
  }

  const isChange = JSON.stringify(ipNumberArr) === JSON.stringify(ObjectsArr?.firewall_object_records);

  return (
    <Styles.SettingPanel>
      <Styles.Header>
        <Styles.CrossIcon>
          <Icon size="large" name="close" onClick={handleCloseModal} />
        </Styles.CrossIcon>
        <Styles.Start>
          <H4>{label}</H4>
          <Icon size="large" name="pencil" />
        </Styles.Start>
      </Styles.Header>
      <Styles.Content>
        <P>{t(item.type == 'port' ? 'vs/firewall.string64' : 'vs/firewall.string48')}</P>
        <Styles.flexObjectContainer>
          <Styles.ObjectInputContainer>
            <Formik
              initialValues={addObjectInitial(item.type)}
              validationSchema={addObjectValidationSchema}
              enableReinitialize
              onSubmit={(values, { resetForm }) => handleClick(values, resetForm)}
            >
              {({ values, errors, handleChange, handleBlur, handleSubmit, touched }) => {
                return (
                  <Form>
                    <Styles.ObjectInputContainers>
                      <Styles.ObjectContainerInput>
                        <H6>{t(item.type == 'port' ? 'vs/firewall.string66' : 'vs/firewall.string49')}</H6>
                        <Input
                          type="text"
                          styling={{ fontSize: '16px', marginTop: '-0.6rem', marginBottom: 0 }}
                          placeholder={t(item.type == 'port' ? 'vs/firewall.string65' : 'vs/firewall.string53')}
                          name={'ipnumber'}
                          focusName={isFocusField}
                          value={values['ipnumber']}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors['ipnumber']}
                          touched={touched['ipnumber']}
                          onEnter={handleSubmit}
                        />
                      </Styles.ObjectContainerInput>
                      <Styles.ObjectContainerButton>
                        <CustomButton
                          type="submit"
                          buttonStyles={{ paddingLeft: '8px' }}
                          title={'vs/firewall.string51'}
                          leftIcon={<Icon name="plus circle" />}
                        />
                      </Styles.ObjectContainerButton>
                    </Styles.ObjectInputContainers>
                  </Form>
                );
              }}
            </Formik>
          </Styles.ObjectInputContainer>

          <Styles.ObjectIpList>
            <div>
              <Styles.ObjectCardPanelSC>
                <Styles.Features>
                  {ipNumberArr?.map((feature, index) => (
                    <Styles.PanelFeature key={index} isDeleted={feature?.isDeleted}>
                      <Styles.PanelFirstFeature
                        // draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                      >
                        <Icon name="check" />
                        <P>{feature.value}</P>
                      </Styles.PanelFirstFeature>
                      <Icon
                        className="delete"
                        name="trash alternate outline"
                        size="large"
                        onClick={() => handleDelete(index)}
                      />
                    </Styles.PanelFeature>
                  ))}
                </Styles.Features>
              </Styles.ObjectCardPanelSC>
            </div>
          </Styles.ObjectIpList>
        </Styles.flexObjectContainer>
        <Styles.ObjectIpListFlex>
          <CustomButton
            isLoading={vsObjectActionLoading || isRefetching}
            disabled={vsObjectActionLoading || isChange || isRefetching}
            title={'vs/firewall.string81'}
            onClick={refetch}
            buttonStyles={{ marginBottom: '10px' }}
          />
          <CustomButton
            isLoading={vsObjectActionLoading}
            disabled={vsObjectActionLoading || isChange}
            title={'vs/firewall.string52'}
            onClick={handleCommit}
            buttonStyles={{ marginLeft: '10px', marginBottom: '10px' }}
          />
        </Styles.ObjectIpListFlex>
      </Styles.Content>
    </Styles.SettingPanel>
  );
};

export default CreateObjectPanel;
